import { Body1, Button, Checkbox, Dialog, DialogActions, DialogTitle } from '@collaborate-health/uikit'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, ListItemText } from '@mui/material'
import moment from 'moment/moment'
import { useSelector, useDispatch } from '@tabeeb/store'
import { useDebouncedState } from '@tabeeb/shared/utils/hooks'
import UserCheckInItem from '@tabeeb/modules/checkIns/components/AddUsersCheckInDialog/UserCheckInItem'
import {
  ContentUserRole,
  CreateTrackingBatchRequest,
  TrackingAnnotationType,
  TrackingEventType,
  TrackingPoint,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { WarningOutlined } from '@mui/icons-material'

import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ContentCheckInsQueryKeys } from '@tabeeb/modules/checkIns/requests'
import { useApi } from '@tabeeb/shared/utils/API'
import { PluginUser } from '../../../../users/types'
import { getUsersListForCurrentUser } from '../../../../users/selectors'
import {
  DateText,
  SelectAllListItem,
  StyledDialogContent,
  StyledList,
  StyledTextField,
  TruncatedText,
} from './AddUsersCheckInDialog.styles'
import { AddUsersCheckInDialogProps } from './AddUsersCheckInDialog.types'

const AddUsersCheckInDialog: React.FC<AddUsersCheckInDialogProps> = ({
  open,
  onClose,
  contentId,
  dateFormat = 'MM/DD/yyyy',
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [selectedUsers, setSelectedUsers] = useState<PluginUser[]>([])
  const users = useSelector<PluginUser[]>(getUsersListForCurrentUser)
  const [search, debouncedSearch, setSearch] = useDebouncedState<string>('', 150)
  const geoLocation = useSelector((state) => state.deviceState.geoLocation)

  const { mainApi } = useApi()

  const { isPending, mutate: createCheckIns } = useMutation({
    mutationFn: (request: CreateTrackingBatchRequest) => mainApi.tracking.createBatchAsync(contentId, request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ContentCheckInsQueryKeys.TrackingSnapshot, contentId] })
      dispatch(onAddSuccessNotification({ message: 'Check-in records created.' }))
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to create check-in records.' }))
    },
  })

  useEffect(() => {
    if (!open) {
      setSelectedUsers([])
    }
  }, [open])

  const geoLocationPresent = useMemo(() => !!(geoLocation.latitude && geoLocation.longitude), [geoLocation])

  const filteredUsers = useMemo(() => {
    const searchText = (debouncedSearch ?? '').toLocaleLowerCase().trim()

    if (!searchText) {
      return users.filter((u) => !u.isDeleted)
    }

    const disallowedRoles = [ContentUserRole.AIUser, ContentUserRole.Reviewer]

    return users.filter((user) => {
      if (user.isDeleted || disallowedRoles.includes(user.role)) {
        return false
      }

      const name = (user.displayName ?? '').toLocaleLowerCase()
      const email = (user.email ?? '').toLocaleLowerCase()
      const phone = (user.phoneNumber ?? '').toLocaleLowerCase()

      return name.includes(searchText) || email.includes(searchText) || phone.includes(searchText)
    })
  }, [users, debouncedSearch])

  const onSelectUser = useCallback((user: PluginUser) => {
    setSelectedUsers((prev) => [...prev, user])
  }, [])

  const onDeselectUser = useCallback((user: PluginUser) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id))
  }, [])

  const onSelectAll = useCallback(() => {
    if (filteredUsers.every((u) => selectedUsers.includes(u))) {
      setSelectedUsers((prev) => prev.filter((u) => !filteredUsers.includes(u)))
    } else {
      setSelectedUsers((prev) => [...prev, ...filteredUsers])
    }
  }, [filteredUsers, selectedUsers])

  const onSubmit = useCallback(() => {
    if (selectedUsers.length === 0 || !geoLocationPresent) {
      return
    }
    const coordinates: TrackingPoint[] = [
      {
        Latitude: geoLocation.latitude!,
        Longitude: geoLocation.longitude!,
      },
    ]

    const request: CreateTrackingBatchRequest = {
      AnnotationType: TrackingAnnotationType.Point,
      EventType: TrackingEventType.CheckIn,
      UserIds: selectedUsers.map((u) => u.id),
      Coordinates: coordinates,
    }

    createCheckIns(request)
  }, [selectedUsers, geoLocationPresent, geoLocation.latitude, geoLocation.longitude, createCheckIns])

  return (
    <Dialog open={open} fullWidth onClose={onClose}>
      <DialogTitle>Add check-in record</DialogTitle>
      <StyledDialogContent>
        {geoLocationPresent && (
          <>
            <Body1>Select one or more users to add their check-in.</Body1>
            <DateText>Date: {moment().format(dateFormat)}</DateText>
            <StyledTextField
              label='Search users'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size='small'
            />
            <StyledList disablePadding dense>
              <SelectAllListItem disableGutters onClick={onSelectAll}>
                <Checkbox checked={filteredUsers.every((u) => selectedUsers.includes(u))} title='Select all' />
                <ListItemText primary={<TruncatedText noWrap>Select all</TruncatedText>} />
              </SelectAllListItem>
              {filteredUsers.map((user) => (
                <UserCheckInItem
                  key={user.id}
                  user={user}
                  selected={selectedUsers.includes(user)}
                  onSelect={onSelectUser}
                  onDeselect={onDeselectUser}
                />
              ))}
            </StyledList>
          </>
        )}
        {!geoLocationPresent && (
          <Alert icon={<WarningOutlined color='warning' />} severity='warning'>
            <Body1>Please, enable geolocation access for this application.</Body1>
          </Alert>
        )}
      </StyledDialogContent>
      <DialogActions>
        <Button color='primary' variant='text' onClick={onClose}>
          Cancel
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={onSubmit}
          disabled={selectedUsers.length === 0 || !geoLocationPresent || isPending}
          loading={isPending}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddUsersCheckInDialog

